import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import { Button, Divider } from '@mui/material'
import clsx from 'clsx'
import React from 'react'

import BrandItem from '@/components/Search/BrandItem'
import ProductCard from '@/components/Search/ProductCard'
import RecentSearchItem from '@/components/Search/RecentSearchItem'

// Panneau de resultats de l'autocomplete : sections marques, gammes, recherches recentes,
// suggestions et produits.
// Ordre colonne gauche : marques > gammes > recentes > suggestions.
// (EsAutocomplete n'envoie pas recentSearches : ses recentes s'affichent en
// dropdown sous la barre de recherche, filtrees par la saisie.)

export type PanelListItem = {
  id: string
  label: string
  onSelect: () => void
  onRemove?: () => void
}

export type PanelBrandItem = {
  id: string
  // Format `Nom###seo_name` attendu par BrandItem
  label: string
}

type Props = {
  isMobile: boolean
  loading?: boolean
  hideLeftColumn?: boolean
  recentSearches?: PanelListItem[]
  brands?: PanelBrandItem[]
  gammes?: PanelBrandItem[]
  suggestions?: PanelListItem[]
  // Hits au format ProductCard (avec _highlightResult pour le surlignage)
  products?: any[]
  // Remplace la liste produits (ex: message "saisissez au moins N caracteres")
  productsPlaceholder?: React.ReactNode
  onSubmit: () => void
  // Navigation clavier (EsAutocomplete) : id de l'element actif + survol
  activeId?: string | null
  onHoverItem?: (id: string) => void
  // Cle produit pour l'id de navigation (defaut : objectID puis product_id)
  productKey?: (product: any) => string
}

const titleClassName = 'text-[#949696] mb-4'
const activeClassName = 'bg-gray-100 rounded'

const defaultProductKey = (product: any): string =>
  String(product?.objectID ?? product?.product_id ?? '')

const SearchResultsPanel: React.FC<Props> = ({
  isMobile,
  loading = false,
  hideLeftColumn = false,
  recentSearches = [],
  brands = [],
  gammes = [],
  suggestions = [],
  products = [],
  productsPlaceholder,
  onSubmit,
  activeId = null,
  onHoverItem,
  productKey = defaultProductKey,
}) => {
  const hoverProps = (id: string) => (onHoverItem ? { onMouseEnter: () => onHoverItem(id) } : {})

  return (
    <div className="overflow-y-auto md:overflow-y-none max-h-[90vh]">
      <div className="p-2 text-center text-sm italic text-gray-600">
        Appuyer sur <b>Entrer</b> pour voir tous les résultats
        {loading && <span className="ml-2 text-gray-400 animate-pulse">…</span>}
      </div>
      <div className={clsx('aa-PanelSections p-4 md:p-8', hideLeftColumn && '!p-2')}>
        <div
          className={clsx(
            'aa-PanelSection--left border-0 border-solid border-r border-gray-200 pr-6 mr-4',
            hideLeftColumn && 'hidden'
          )}
        >
          {brands.length > 0 && (
            <div>
              <div className={titleClassName}>Marques</div>
              <div className="grid grid-cols-2 gap-2">
                {brands.map(item => (
                  <div
                    key={item.id}
                    className={clsx(item.id === activeId && activeClassName)}
                    {...hoverProps(item.id)}
                  >
                    <BrandItem hit={{ label: item.label }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {gammes.length > 0 && (
            <div className="mt-4">
              <div className={titleClassName}>Gammes</div>
              <div className="grid grid-cols-2 gap-2">
                {gammes.map(item => (
                  <div key={item.id}>
                    <BrandItem hit={{ label: item.label }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {recentSearches.length > 0 && (
            <div className="mt-4">
              <div className={titleClassName}>Mes recherches récentes</div>
              <div className="aa-List">
                {recentSearches.map(item => (
                  <div
                    key={item.id}
                    className={clsx(item.id === activeId && activeClassName)}
                    {...hoverProps(item.id)}
                  >
                    <RecentSearchItem
                      hit={{
                        label: item.label,
                        onTapAhead: item.onSelect,
                        onRemove: item.onRemove,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="mt-4">
              <div className="aa-SourceHeaderTitle">Suggestions</div>
              <div className="aa-SourceHeaderLine" />
              <div className="aa-List">
                {suggestions.map(item => (
                  <div
                    key={item.id}
                    className={clsx(
                      'aa-ItemWrapper cursor-pointer',
                      item.id === activeId && activeClassName
                    )}
                    onClick={item.onSelect}
                    {...hoverProps(item.id)}
                  >
                    <div className="aa-ItemContent">
                      <div className="aa-ItemIcon aa-ItemIcon--noBorder" />
                      <div className="aa-ItemContentBody">
                        <div className="aa-ItemContentTitle">{item.label}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="md:max-h-[60vh] md:overflow-y-auto h-full w-full min-w-0 overflow-x-hidden">
          <div className="flex justify-between items-center mb-4">
            <div className={clsx(titleClassName, !isMobile && 'ml-24', 'mb-0')}>Produits</div>
            <Button variant="contained" color="secondary" onClick={onSubmit}>
              Voir tous les produits <ArrowRightAltIcon className="ml-2" />
            </Button>
          </div>
          {productsPlaceholder ??
            (products.length > 0 ? (
              <div className="!space-y-4 w-full min-w-0 max-w-full">
                {products.map(product => {
                  const id = `product:${productKey(product)}`
                  return (
                    <React.Fragment key={id}>
                      <div
                        className={clsx(onHoverItem && 'p-1', id === activeId && activeClassName)}
                        {...hoverProps(id)}
                      >
                        <ProductCard hit={product} variant={isMobile ? 'card' : 'row'} />
                      </div>
                      <Divider />
                    </React.Fragment>
                  )
                })}
              </div>
            ) : (
              !loading && (
                <div className="aa-SourceNoResults">Pas de résultats pour la recherche</div>
              )
            ))}
        </div>
      </div>
    </div>
  )
}

export default SearchResultsPanel
